<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-md-4" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12">

            <div v-if="loading" class="d-flex justify-center py-12">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>

            <template v-else-if="sheet">
              <div class="d-flex align-center mb-4" style="gap: 12px">
                <h2>
                  <v-icon class="mr-2">mdi-table</v-icon>
                  {{ sheet.name }}
                </h2>
                <v-chip small class="grey--text">{{ sheet.rows.length }} lignes</v-chip>
                <v-spacer />
                <v-btn
                  color="primary"
                  elevation="0"
                  :loading="preprocessLoading"
                  @click="runPreprocess"
                >
                  <v-icon left>mdi-tag-multiple-outline</v-icon>
                  Classifier les types
                </v-btn>
              </div>

              <!-- Preprocess result banner -->
              <v-alert
                v-if="preprocessResult"
                type="success"
                dense
                text
                class="mb-4"
                dismissible
                @input="preprocessResult = null"
              >
                {{ preprocessResult.row_count }} lignes classifiées —
                <span class="font-weight-medium">{{ preprocessResult.preprocessed_at | formatDate }}</span>
              </v-alert>
              <v-alert
                v-if="preprocessError"
                type="error"
                dense
                text
                class="mb-4"
                dismissible
                @input="preprocessError = null"
              >
                {{ preprocessError }}
              </v-alert>

              <v-card elevation="0">
                <v-card-text class="pa-0">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Filtrer…"
                    outlined dense hide-details
                    class="ma-3"
                    style="max-width: 340px"
                  />
                  <v-data-table
                    :headers="headers"
                    :items="annotatedRows"
                    :search="search"
                    class="elevation-0"
                    dense
                    :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
                    :items-per-page="50"
                  >
                    <template v-for="col in sheet.columns" v-slot:[`item.${col}`]="{ item }">
                      <span :key="col" :class="item[col] === null || item[col] === undefined ? 'grey--text' : ''">
                        {{ item[col] !== null && item[col] !== undefined ? item[col] : '—' }}
                      </span>
                    </template>
                    <template v-slot:[`item._type`]="{ item }">
                      <v-chip
                        x-small
                        :color="typeColor(item._type)"
                        dark
                        label
                      >{{ item._type || '—' }}</v-chip>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </template>

          </v-col>
        </v-row>
      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import { fetchAdminFileSpreadsheet, preprocessAdminFile } from '@/services/adminApi'

export default {
  name: 'AdminSpreadsheetView',
  components: { PageBack, MainLayout },

  filters: {
    formatDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },

  data() {
    return {
      loading: false,
      error: null,
      sheet: null,
      search: '',
      preprocessLoading: false,
      preprocessResult: null,
      preprocessError: null,
      rowTypes: null,  // { "19648": "plaque", ... } keyed by Code article string
    }
  },

  computed: {
    headers() {
      if (!this.sheet) return []
      const cols = this.sheet.columns.map(col => ({ text: col, value: col, sortable: true }))
      if (this.rowTypes) {
        cols.unshift({ text: 'Type produit', value: '_type', sortable: true, width: '130px' })
      }
      return cols
    },

    annotatedRows() {
      if (!this.sheet) return []
      if (!this.rowTypes) return this.sheet.rows
      return this.sheet.rows.map((row, i) => {
        const key = row['Code article'] != null ? String(Math.trunc(row['Code article'])) : `row_${i}`
        return { ...row, _type: this.rowTypes[key] || null }
      })
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.sheet = await fetchAdminFileSpreadsheet(this.$route.params.id)
      } catch (e) {
        this.error = e?.data?.detail || 'Erreur lors du chargement du fichier.'
      } finally {
        this.loading = false
      }
    },

    async runPreprocess() {
      this.preprocessLoading = true
      this.preprocessResult = null
      this.preprocessError = null
      try {
        const result = await preprocessAdminFile(this.$route.params.id)
        this.preprocessResult = result
        this.rowTypes = result.row_types || null
      } catch (e) {
        this.preprocessError = e?.data?.error || 'Erreur lors de la classification.'
      } finally {
        this.preprocessLoading = false
      }
    },

    typeColor(type) {
      const palette = {
        plaque: 'blue darken-1',
        vis: 'teal darken-1',
        montant: 'indigo darken-1',
        rail: 'cyan darken-2',
        enduit: 'orange darken-1',
        bande: 'deep-purple darken-1',
        isolant: 'green darken-1',
        colle: 'brown darken-1',
        cheville: 'red darken-1',
        autre: 'grey darken-1',
      }
      return palette[type] || 'grey darken-1'
    },
  },
}
</script>
